/**
 * Family Money — how money is shared across the household.
 * Quiet rows drawn from the existing household member state.
 * No dashboards, no totals, progressive disclosure only.
 */
import { useMemo, useState } from "react";
import { useHomeOSState } from "../state/HomeOSStateProvider";

const inter = { fontFamily: "Inter, system-ui, sans-serif" };

export function FamilyMoneySection({ bankTxns = [], bills = [], goals = [] }) {
  const { members } = useHomeOSState();
  const [expanded, setExpanded] = useState(null);

  const rows = useMemo(() => {
    const isHouseholdLabel = (l) => !l || /whole (household|family)/i.test(l);

    const householdItems = [
      ...bills.filter((b) => isHouseholdLabel(b.forWhom)).map((b) => ({ kind: "Bill", name: b.name, meta: b.amount })),
      ...goals.filter((g) => isHouseholdLabel(g.owner)).map((g) => ({ kind: "Goal", name: g.name, meta: `${g.current} of ${g.target}` })),
      ...bankTxns.filter((t) => !t.assigned_to || t.assigned_to === "household").slice(0, 6)
        .map((t) => ({ kind: "Bank", name: t.merchant || t.description, meta: t.category_name || "" })),
    ];

    const householdSummary = [
      bills.filter((b) => isHouseholdLabel(b.forWhom)).length > 0 && `${bills.filter((b) => isHouseholdLabel(b.forWhom)).length} bill${bills.length === 1 ? "" : "s"}`,
      goals.filter((g) => isHouseholdLabel(g.owner)).length > 0 && `${goals.filter((g) => isHouseholdLabel(g.owner)).length} goal${goals.length === 1 ? "" : "s"}`,
      bankTxns.filter((t) => !t.assigned_to || t.assigned_to === "household").length > 0 && "everyday spending",
    ].filter(Boolean).join(" · ") || "Most things live here by default";

    const memberRows = (members || []).map((m) => {
      const name = m.name;
      const theirTxns = bankTxns.filter((t) => t.assigned_to_label === name);
      const theirBills = bills.filter((b) => b.forWhom === name);
      const theirGoals = goals.filter((g) => g.owner === name);
      const categorySignals = [...new Set(theirTxns.map((t) => t.category_name).filter(Boolean))].slice(0, 3);
      const summaryParts = [
        ...categorySignals,
        theirBills.length > 0 && `${theirBills.length} bill${theirBills.length === 1 ? "" : "s"}`,
        theirGoals.length > 0 && `${theirGoals.length} goal${theirGoals.length === 1 ? "" : "s"}`,
      ].filter(Boolean);
      return {
        id: String(m.id || name),
        name,
        role: m.role || "",
        summary: summaryParts.length > 0 ? summaryParts.join(" · ") : "Nothing assigned yet",
        items: [
          ...theirBills.map((b) => ({ kind: "Bill", name: b.name, meta: b.amount })),
          ...theirGoals.map((g) => ({ kind: "Goal", name: g.name, meta: `${g.current} of ${g.target}` })),
          ...theirTxns.slice(0, 6).map((t) => ({ kind: "Bank", name: t.merchant || t.description, meta: t.category_name || "" })),
        ],
      };
    });

    return [
      { id: "household", name: "Whole household", role: "", summary: householdSummary, items: householdItems.slice(0, 8) },
      ...memberRows,
    ];
  }, [members, bankTxns, bills, goals]);

  return (
    <section id="financials-family-money" data-testid="financials-family-money" className="mt-14">
      <div className="min-w-0">
        <h2
          className="text-[22px] sm:text-[24px] leading-[1.15] tracking-[-0.01em]"
          style={{ fontFamily: "var(--ara-font-display), 'Times New Roman', serif", color: "var(--ara-ink)", fontWeight: 400 }}
        >
          Family money
        </h2>
        <p
          className="mt-1.5 italic text-[12.5px] leading-[1.5] max-w-[52ch]"
          style={{ fontFamily: "var(--ara-font-display), 'Times New Roman', serif", color: "var(--ara-ink-muted)", fontWeight: 400 }}
        >
          How money is shared across the household.
        </p>
      </div>

      {(members || []).length === 0 && bankTxns.length === 0 ? (
        <p className="mt-6 text-[13.5px] italic" style={{ ...inter, color: "var(--ara-ink-soft)", fontWeight: 400 }} data-testid="family-money-empty">
          Add your family in the Family Room and ARA will show how money relates to each person.
        </p>
      ) : (
        <div className="mt-6">
          {rows.map((r, i) => {
            const isOpen = expanded === r.id;
            const isLast = i === rows.length - 1;
            return (
              <div key={r.id} style={{ borderBottom: isLast ? "none" : "1px solid var(--ara-line-soft)" }} data-testid={`family-money-row-${r.id}`}>
                <button
                  type="button"
                  onClick={() => setExpanded(isOpen ? null : r.id)}
                  className="w-full text-left py-4 min-h-[44px] flex items-baseline justify-between gap-4 transition-ara hover:opacity-80"
                  style={{ background: "none", border: "none", cursor: "pointer" }}
                >
                  <span className="min-w-0">
                    <span className="block text-[14.5px] leading-tight" style={{ ...inter, color: "var(--ara-ink)", fontWeight: 500 }}>
                      {r.name}
                    </span>
                    <span className="block mt-1 text-[12.5px]" style={{ ...inter, color: "var(--ara-ink-soft)", fontWeight: 400 }}>
                      {r.summary}
                    </span>
                  </span>
                  <span className="text-[12px] whitespace-nowrap" style={{ ...inter, color: "var(--ara-ink-soft)" }} aria-hidden>
                    {isOpen ? "Close" : "View →"}
                  </span>
                </button>
                {isOpen && (
                  <div className="pb-5 space-y-2" data-testid={`family-money-detail-${r.id}`}>
                    {r.items.length === 0 ? (
                      <p className="text-[12.5px] italic" style={{ ...inter, color: "var(--ara-ink-soft)" }}>
                        Nothing here yet. Assign a bill, goal or transaction to {r.name === "Whole household" ? "the household" : r.name}.
                      </p>
                    ) : r.items.map((it, j) => (
                      <p key={j} className="text-[13px] leading-[1.6]" style={{ ...inter, color: "var(--ara-ink-muted)", fontWeight: 400 }}>
                        <span style={{ color: "var(--ara-ink-soft)" }}>{it.kind} · </span>
                        {it.name}
                        {it.meta ? <span style={{ color: "var(--ara-ink-soft)" }}> — {it.meta}</span> : null}
                      </p>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
